import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, switchMap } from "rxjs";

import { TenantsApiService } from "../../../../shared/services/tenants.api.service";
import { ThemesApiService } from "../../../../admin/themes/services/themes.api.service";
import { ThemeResponse } from "../../../../admin/themes/models/theme.model";
import { SubmissionFreeformApiService } from "../../services/submission-freeform.api.service";
import { SubmissionFreeformResponse } from "../../models/submission-freeform.model";
import { PollApiService } from "../../../services/poll.api.service";
import { PollResponse, PollResultsResponse } from "../../../models/poll.model";
import { PollId } from "../../../../shared/models/entity.model";

export interface HostViewData {
    submissions: SubmissionFreeformResponse[];
    theme: ThemeResponse;
}

export interface HostPollViewData {
    poll: PollResponse;
    results: PollResultsResponse;
    theme: ThemeResponse;
}

@Injectable({ providedIn: "root" })
export class HostApiService {
    private readonly tenantsService = inject(TenantsApiService);
    private readonly themesService = inject(ThemesApiService);
    private readonly submissionFreeformService = inject(SubmissionFreeformApiService);
    private readonly pollService = inject(PollApiService);

    getHostViewData(tenantId: string, interactiveId: string): Observable<HostViewData> {
        return this.tenantsService.getTenant(tenantId).pipe(
            switchMap(tenant =>
                forkJoin({
                    submissions: this.submissionFreeformService.getPrioritized(interactiveId), // All submissions will have an ordered priority
                    theme: this.themesService.getTheme(tenant.themeId) // We need to fetch the tenant to get the theme
                })
            )
        );
    }

    /**
     * Poll sibling of `getHostViewData`: the poll itself (for the prompt), its results, and the
     * theme that carries the host wallpaper/logo.
     *
     * `interactiveId` arrives already stripped of its `IP:` prefix from `NavbarService`, which is
     * what `getResults` wants. `get()` strips again internally, which is a no-op on a bare uuid.
     */
    getHostPollViewData(tenantId: string, interactiveId: string): Observable<HostPollViewData> {
        return this.tenantsService.getTenant(tenantId).pipe(
            switchMap(tenant =>
                forkJoin({
                    poll: this.pollService.get(interactiveId as PollId),
                    results: this.pollService.getResults(interactiveId),
                    theme: this.themesService.getTheme(tenant.themeId) // We need to fetch the tenant to get the theme
                })
            )
        );
    }

    /**
     * Results only, for the host view's refresh tick — re-fetching the poll, tenant and theme
     * every few seconds would be wasteful, and none of them can change mid-show.
     */
    getPollResults(interactiveId: string): Observable<PollResultsResponse> {
        return this.pollService.getResults(interactiveId);
    }
}
